import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Inbox, Loader2, LogOut, RefreshCw, Trash2, Phone, Mail, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = { new: "جديد", in_progress: "قيد التنفيذ", done: "مكتمل" };
const STATUS_COLORS = {
  new: "bg-gold/15 text-gold border-gold/30",
  in_progress: "bg-sky-400/15 text-sky-300 border-sky-400/30",
  done: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, done: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const client = useCallback(() => {
    const token = localStorage.getItem("bs_token");
    return axios.create({ headers: { Authorization: `Bearer ${token}` } });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        client().get(`${API}/admin/leads`, { params: filter === "all" ? {} : { status: filter } }),
        client().get(`${API}/admin/stats`),
      ]);
      setLeads(leadsRes.data.leads);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("bs_token");
        navigate("/admin/login");
      } else {
        toast.error("تعذر تحميل الطلبات");
      }
    } finally {
      setLoading(false);
    }
  }, [client, filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await client().patch(`${API}/admin/leads/${id}`, { status });
      toast.success("تم تحديث الحالة");
      load();
    } catch {
      toast.error("تعذر تحديث الحالة");
    }
  };

  const remove = async (id) => {
    try {
      await client().delete(`${API}/admin/leads/${id}`);
      toast.success("تم حذف الطلب");
      load();
    } catch {
      toast.error("تعذر حذف الطلب");
    }
  };

  const logout = () => {
    localStorage.removeItem("bs_token");
    localStorage.removeItem("bs_user");
    navigate("/admin/login");
  };

  const exportExcel = async () => {
    try {
      const res = await client().get(`${API}/admin/leads/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "barqsafe-leads.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("تم تنزيل ملف الطلبات");
    } catch {
      toast.error("تعذر تصدير الطلبات");
    }
  };

  const statCards = [
    { key: "total", label: "إجمالي الطلبات", value: stats.total },
    { key: "new", label: "جديدة", value: stats.new },
    { key: "in_progress", label: "قيد التنفيذ", value: stats.in_progress },
    { key: "done", label: "مكتملة", value: stats.done },
  ];

  return (
    <div className="min-h-screen bg-navy-900" dir="rtl" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-navy-950/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Barq Safe" className="h-11 w-11 object-contain rounded-xl bg-white/95 p-1" />
            <div>
              <div className="font-display font-extrabold">لوحة تحكم برق سيف</div>
              <div className="text-[11px] text-white/40">متابعة طلبات العملاء</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportExcel} data-testid="export-leads-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-gold text-sm hover:bg-gold/10 transition-colors">
              <FileSpreadsheet size={15} />
              تصدير Excel
            </button>
            <button onClick={load} data-testid="refresh-leads-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-white/70 text-sm hover:border-gold/50 hover:text-gold transition-colors">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              تحديث
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-red-400/30 text-red-300 text-sm hover:bg-red-400/10 transition-colors">
              <LogOut size={15} />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {statCards.map((s) => (
            <div key={s.key} className="glass rounded-2xl p-6" data-testid={`stat-card-${s.key}`}>
              <div className="text-xs text-white/50">{s.label}</div>
              <div className="font-display text-4xl font-black gold-text mt-2">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          {[["all", "الكل"], ["new", "جديدة"], ["in_progress", "قيد التنفيذ"], ["done", "مكتملة"]].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)} data-testid={`filter-${key}`}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${
                filter === key ? "bg-gold text-navy-900" : "border border-white/15 text-white/60 hover:border-gold/40 hover:text-gold"
              }`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-gold" /></div>
        ) : leads.length === 0 ? (
          <div className="glass rounded-3xl py-20 flex flex-col items-center text-white/40" data-testid="empty-leads">
            <Inbox size={44} className="mb-4" />
            لا توجد طلبات حالياً
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {leads.map((lead) => (
              <div key={lead.id} className="glass rounded-2xl p-6" data-testid={`lead-card-${lead.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-bold text-lg">{lead.name}</h3>
                      <span className={`text-xs px-3 py-1 rounded-full border ${STATUS_COLORS[lead.status]}`} data-testid={`lead-status-${lead.id}`}>
                        {STATUS_LABELS[lead.status]}
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gold">{lead.service}</div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/60">
                      <a href={`tel:${lead.phone}`} dir="ltr" className="inline-flex items-center gap-1.5 hover:text-gold transition-colors"><Phone size={13} />{lead.phone}</a>
                      {lead.email && <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors"><Mail size={13} />{lead.email}</a>}
                      <span className="text-white/30 text-xs">{new Date(lead.created_at).toLocaleString("ar-SA")}</span>
                    </div>
                    {lead.message && <p className="mt-3 text-sm text-white/50 leading-relaxed max-w-2xl">{lead.message}</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <select value={lead.status} onChange={(e) => updateStatus(lead.id, e.target.value)} data-testid={`lead-status-select-${lead.id}`}
                      className="rounded-xl bg-navy-900/70 border border-white/10 px-3 py-2.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-gold/60">
                      {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v} className="bg-navy-800">{l}</option>)}
                    </select>
                    <button onClick={() => remove(lead.id)} data-testid={`lead-delete-${lead.id}`}
                      className="p-2.5 rounded-xl border border-red-400/30 text-red-300 hover:bg-red-400/10 transition-colors" aria-label="حذف">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
